import { useMemo } from "react";
import { Users, PhoneCall, ShoppingCart, BadgeCheck } from "lucide-react";

// Minimalny ksztalt karty potrzebny do statystyk (podzbior CrmLeadCard z sales-kanban).
type StatLead = {
  stage: string;
  source: string | null;
  qualification: "qualified" | "unqualified" | null;
};

type StageDef = { key: string; label: string };

type Props = {
  leads: StatLead[];
  stages: StageDef[];
};

// Etapy oznaczajace "doszlo do rozmowy" oraz "sprzedaz".
const CALL_STAGES = new Set(["booked", "showed", "proposal", "won"]);
const WON_STAGE = "won";
const NO_SOURCE = "(brak zrodla)";

function pct(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

export default function CrmStats({ leads, stages }: Props) {
  const stats = useMemo(() => {
    const total = leads.length;
    const calls = leads.filter((l) => CALL_STAGES.has(l.stage)).length;
    const sales = leads.filter((l) => l.stage === WON_STAGE).length;
    const qualified = leads.filter((l) => l.qualification === "qualified").length;
    const unqualified = leads.filter((l) => l.qualification === "unqualified").length;

    const byStage = stages.map((s) => ({
      ...s,
      count: leads.filter((l) => l.stage === s.key).length,
    }));
    const maxStage = Math.max(1, ...byStage.map((s) => s.count));

    // Agregacja po zrodle: leady / rozmowy / sprzedaze + konwersja rozmowa->sprzedaz.
    const sourceMap = new Map<
      string,
      { leads: number; calls: number; sales: number; qualified: number }
    >();
    for (const l of leads) {
      const key = l.source?.trim() || NO_SOURCE;
      const row = sourceMap.get(key) ?? { leads: 0, calls: 0, sales: 0, qualified: 0 };
      row.leads += 1;
      if (CALL_STAGES.has(l.stage)) row.calls += 1;
      if (l.stage === WON_STAGE) row.sales += 1;
      if (l.qualification === "qualified") row.qualified += 1;
      sourceMap.set(key, row);
    }
    const bySource = [...sourceMap.entries()]
      .map(([source, v]) => ({ source, ...v }))
      .sort((a, b) => b.calls - a.calls || b.leads - a.leads);

    return { total, calls, sales, qualified, unqualified, byStage, maxStage, bySource };
  }, [leads, stages]);

  const kpis = [
    { label: "Leady", value: stats.total, icon: Users, accent: "text-white" },
    { label: "Rozmowy", value: stats.calls, icon: PhoneCall, accent: "text-sky-300" },
    { label: "Sprzedaze", value: stats.sales, icon: ShoppingCart, accent: "text-emerald-300" },
    {
      label: "% Kwalifikowanych",
      value: `${pct(stats.qualified, stats.total)}%`,
      icon: BadgeCheck,
      accent: "text-[#b79bff]",
    },
  ];

  if (stats.total === 0) {
    return (
      <div className="text-center py-16 text-white/30 text-sm">
        Brak leadow w CRM. Zsynchronizuj leady, aby zobaczyc statystyki.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-center gap-2 text-white/40 text-xs">
                <Icon className="w-3.5 h-3.5" />
                {k.label}
              </div>
              <div className={`mt-2 text-2xl font-bold ${k.accent}`}>{k.value}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Lejek po etapach */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <h3 className="text-sm font-semibold text-white/90 mb-3">Leady wg etapu</h3>
          <div className="space-y-2">
            {stats.byStage.map((s) => (
              <div key={s.key} className="flex items-center gap-3">
                <span className="w-32 shrink-0 text-xs text-white/50 truncate">{s.label}</span>
                <div className="flex-1 h-5 rounded bg-white/5 overflow-hidden">
                  <div
                    className="h-full bg-[#6E3AFF]/60 rounded"
                    style={{ width: `${(s.count / stats.maxStage) * 100}%` }}
                  />
                </div>
                <span className="w-14 shrink-0 text-right text-xs text-white/70">
                  {s.count} <span className="text-white/30">({pct(s.count, stats.total)}%)</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Kwalifikacja */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <h3 className="text-sm font-semibold text-white/90 mb-3">Kwalifikacja leadow</h3>
          <div className="space-y-3 mt-1">
            {[
              { label: "Kwalifikowani", value: stats.qualified, cls: "bg-emerald-400/60" },
              { label: "Niekwalifikowani", value: stats.unqualified, cls: "bg-rose-400/60" },
              {
                label: "Nieoznaczeni",
                value: stats.total - stats.qualified - stats.unqualified,
                cls: "bg-white/20",
              },
            ].map((row) => (
              <div key={row.label} className="flex items-center gap-3">
                <span className="w-32 shrink-0 text-xs text-white/50">{row.label}</span>
                <div className="flex-1 h-5 rounded bg-white/5 overflow-hidden">
                  <div
                    className={`h-full rounded ${row.cls}`}
                    style={{ width: `${pct(row.value, stats.total)}%` }}
                  />
                </div>
                <span className="w-14 shrink-0 text-right text-xs text-white/70">
                  {row.value}{" "}
                  <span className="text-white/30">({pct(row.value, stats.total)}%)</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Zrodla */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
        <h3 className="text-sm font-semibold text-white/90 mb-3">Wg zrodla</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/40 text-[11px] uppercase tracking-wider border-b border-white/10 [&_th]:py-2 [&_th]:px-2 [&_th]:font-medium">
                <th className="text-left">Zrodlo</th>
                <th className="text-right">Leady</th>
                <th className="text-right">Rozmowy</th>
                <th className="text-right">Sprzedaze</th>
                <th className="text-right">Konw. rozmowa→sprzedaz</th>
                <th className="text-right">% Kwalif.</th>
              </tr>
            </thead>
            <tbody className="[&_td]:py-2 [&_td]:px-2">
              {stats.bySource.map((row) => (
                <tr key={row.source} className="border-b border-white/5">
                  <td className="text-left text-white/80 truncate max-w-[200px]">{row.source}</td>
                  <td className="text-right text-white/70">{row.leads}</td>
                  <td className="text-right text-white/70">{row.calls}</td>
                  <td className="text-right text-emerald-300">{row.sales}</td>
                  <td className="text-right text-white/50">{pct(row.sales, row.calls)}%</td>
                  <td className="text-right text-white/50">{pct(row.qualified, row.leads)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
